
import { useState, useCallback } from 'react';
import { BlogPostFormData } from '@/types/blog';
import { useFormValidation } from './editor/useFormValidation';
import { useUnsavedChangesPrompt } from './useUnsavedChangesPrompt';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface UseBlogPostFormProps {
  id: string;
  isNew: boolean;
  initialData?: BlogPostFormData;
}

export const useBlogPostForm = ({ id, isNew, initialData }: UseBlogPostFormProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { validateForm, prepareForSave } = useFormValidation();
  
  const [formData, setFormData] = useState<BlogPostFormData>(initialData || {
    title: "",
    content: "",
    excerpt: "",
    image_url: "",
    slug: "",
    status: "draft",
    scheduled_for: null,
    published_at: null,
    metadata: {
      author: "",
      categories: [],
      tags: [],
      featured: false,
      seo_description: "",
      sources: []
    }
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Use the unsaved changes prompt hook
  const promptIfUnsaved = useUnsavedChangesPrompt(
    hasUnsavedChanges,
    "You have unsaved changes. Are you sure you want to leave?"
  );

  // Handle form changes
  const handleFormChange = useCallback((field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    
    setHasUnsavedChanges(true);
  }, []);

  // Handle saving the post
  const handleSave = useCallback(async () => {
    if (!validateForm(formData)) return;

    try {
      setIsSubmitting(true);
      
      // Prepare the data for Supabase
      const postDataToSave = prepareForSave(formData);
      
      let response;
      
      if (isNew) {
        // Insert new post
        response = await supabase.from('blog_posts').insert(postDataToSave);
      } else {
        // Update existing post
        response = await supabase
          .from('blog_posts')
          .update(postDataToSave)
          .eq('id', id);
      }
      
      if (response.error) throw response.error;
      
      // Invalidate queries to refresh data throughout the application
      await queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      await queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
      
      if (id && id !== 'new') {
        await queryClient.invalidateQueries({ queryKey: ['blog-post', id] });
      }
      
      // Reset unsaved changes flag
      setHasUnsavedChanges(false);
      
      toast.success(isNew ? "Post created successfully" : "Post updated successfully");
      
      // Navigate back to the blog management page after creating/editing
      if (isNew) {
        navigate('/dashboard/blog/posts');
      }

      return true;
    } catch (error: any) {
      console.error("Error saving post:", error);
      toast.error(`Failed to save post: ${error.message}`);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isNew, id, validateForm, prepareForSave, queryClient, navigate]);

  // Handle deleting the post
  const handleDelete = useCallback(async () => {
    if (!isNew && id) {
      if (window.confirm("Are you sure you want to delete this post? This action cannot be undone.")) {
        try {
          setIsSubmitting(true);
          const { error } = await supabase
            .from('blog_posts')
            .delete()
            .eq('id', id);
          
          if (error) throw error;
          
          // Invalidate queries to refresh data
          await queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
          await queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
          
          toast.success("Post deleted successfully");
          navigate('/dashboard/blog/posts');
          return true;
        } catch (error: any) {
          console.error("Error deleting post:", error);
          toast.error(`Failed to delete post: ${error.message}`);
          return false;
        } finally {
          setIsSubmitting(false);
        }
      }
    }
    return false;
  }, [isNew, id, queryClient, navigate]);

  return {
    formData,
    setFormData,
    isSubmitting,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    handleFormChange,
    handleSave,
    handleDelete,
    promptIfUnsaved
  };
};
